import React from "react";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/Card";
import { Button, type ButtonProps } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

export interface FormCardButton
  extends Omit<ButtonProps, "onClick" | "children"> {
  label: string;
  loadingLabel?: string;
  onClick: () => void;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

interface FormCardProps {
  title: string;
  description: string;
  children: React.ReactNode;
  previousButton?: FormCardButton;
  cancelButton?: FormCardButton;
  saveAsDraftButton?: FormCardButton;
  nextButton?: FormCardButton;
  className?: string;
}

/**
 * FormCard - A reusable card component for multi-step forms and dialogs
 *
 * Provides a consistent layout with gradient header, content area, and flexible button configuration.
 * Handles all styling, spacing, and button states automatically.
 *
 * FEATURES:
 * - Gradient header with title and description
 * - Auto-width container that wraps content
 * - Up to 4 optional buttons with flexible positioning
 * - Loading states with spinners and custom labels
 * - Disabled states with proper visual feedback (opacity-60 for accessibility)
 * - Icons support for buttons
 * - Full Button component prop inheritance (size, type, etc.)
 * - Consistent spacing and styling across the app
 *
 * BUTTON LAYOUT:
 * [Previous] [Cancel]                    [Save as Draft] [Next →]
 *     ↑ Left side buttons                      ↑ Right side buttons
 *
 * USAGE EXAMPLES:
 *
 * // Simple 2-button form
 * <FormCard
 *   title="Choose Event Type"
 *   description="Select the type of event you want to create"
 *   cancelButton={{ label: "Cancel", onClick: handleCancel, variant: "outline" }}
 *   nextButton={{ label: "Next →", onClick: handleNext, disabled: !isValid }}
 * >
 *   <YourFormContent />
 * </FormCard>
 *
 * // Complex 4-button form with loading and all Button props
 * <FormCard
 *   title="Event Configuration"
 *   description="Configure your event settings"
 *   previousButton={{
 *     label: "Previous",
 *     onClick: handlePrevious,
 *     variant: "outline",
 *     size: "sm"  // ← Supports all Button props
 *   }}
 *   cancelButton={{ label: "Cancel", onClick: handleCancel, variant: "ghost" }}
 *   saveAsDraftButton={{
 *     label: "Save as Draft",
 *     onClick: handleSaveAsDraft,
 *     variant: "outline",
 *     icon: <PenLine className="h-4 w-4" />,
 *     type: "button"  // ← Button props work automatically
 *   }}
 *   nextButton={{
 *     label: "Submit Event",
 *     loadingLabel: "Submitting...",
 *     onClick: handleSubmit,
 *     loading: isSubmitting,
 *     size: "lg"  // ← All Button sizing options available
 *   }}
 * >
 *   <YourFormContent />
 * </FormCard>
 *
 * BUTTON VARIANTS:
 * - default: Primary action (solid background) - use for main actions
 * - outline: Secondary action (border only) - use for previous, save draft
 * - ghost: Tertiary action (minimal styling) - use for cancel, skip
 *
 * BUTTON PROPS INHERITANCE:
 * FormCardButton extends ButtonProps, so you get all Button features:
 * - variant: "default" | "outline" | "ghost" | "destructive" | "secondary" | "link"
 * - size: "default" | "sm" | "lg" | "icon"
 * - type: "button" | "submit" | "reset"
 * - Plus all native button attributes (aria-*, data-*, etc.)
 *
 * STYLING NOTES:
 * - FormCard provides structure only - content components handle their own spacing
 * - Use my-10 mx-4 (40px vertical, 16px horizontal) for consistent content padding
 * - All buttons automatically get proper spacing and positioning
 * - Loading states show spinner + custom text (buttons remain enabled during loading)
 * - Disabled states use opacity-60 (not opacity-30) for better accessibility
 * - Icons should be 16x16 (h-4 w-4) for consistency
 * - Default button classes are applied but can be overridden with className prop
 *
 * @param title - Header title text
 * @param description - Header description text
 * @param children - Content to render in the card body
 * @param previousButton - Optional left-side previous button (extends ButtonProps)
 * @param cancelButton - Optional left-side cancel button (extends ButtonProps)
 * @param saveAsDraftButton - Optional right-side save as draft button (extends ButtonProps)
 * @param nextButton - Optional right-side next/submit button (extends ButtonProps)
 * @param className - Additional CSS classes for the container
 */

export const FormCard = ({
  title,
  description,
  children,
  previousButton,
  cancelButton,
  saveAsDraftButton,
  nextButton,
  className,
}: FormCardProps) => {
  const renderButton = (button?: FormCardButton, defaultClass = "") => {
    if (!button) return null;

    const {
      label,
      loadingLabel,
      onClick,
      loading,
      icon,
      className: buttonClassName,
      iconRight,
      disabled,
      ...buttonProps
    } = button;

    const displayLabel = loading && loadingLabel ? loadingLabel : label;
    const isButtonDisabled = disabled && !loading;

    return (
      <Button
        {...buttonProps}
        onClick={loading ? undefined : onClick}
        disabled={isButtonDisabled}
        className={cn(defaultClass, buttonClassName)}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {displayLabel}
          </>
        ) : (
          <>
            {icon}
            {displayLabel}
            {iconRight}
          </>
        )}
      </Button>
    );
  };

  const hasAnyButton =
    previousButton || cancelButton || saveAsDraftButton || nextButton;

  return (
    <div className={cn("w-fit", className)}>
      <Card className="rounded-lg border border-border bg-background shadow-sm">
        <CardHeader
          className="space-y-0 rounded-t-lg p-6"
          style={{
            background: "var(--form-card-gradient)",
          }}
        >
          <CardTitle className="mb-2 text-[16px] leading-none font-semibold text-primary">
            {title}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {description}
          </CardDescription>
        </CardHeader>

        <div className="h-px bg-border"></div>

        <div>{children}</div>

        {hasAnyButton && (
          <>
            <div className="h-px bg-border"></div>

            <div className="py-5">
              <div className="flex items-center justify-between">
                <div className="ml-6 flex gap-2">
                  {renderButton(previousButton)}
                  {renderButton(cancelButton)}
                </div>
                <div className="mr-6 flex gap-2">
                  {renderButton(saveAsDraftButton)}
                  {renderButton(nextButton)}
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
